import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Polyline,
  Tooltip,
  Popup,
  Pane,
  LayerGroup,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import { colors } from '../theme';
import useFarmContext from './useFarmContext';
import ensureLeafletIcons from './leafletConfig';
import { formatLocal, formatCoordinate, isValidCoordinate } from './utils';

const API_BASE = process.env.REACT_APP_API_URL || '';
const PAGE_SIZE = 200;
const MAX_PAGES = 10;

const TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
const MS_IN_DAY = 24 * 60 * 60 * 1000;
const DEFAULT_WINDOW_DAYS = 30;
const DEFAULT_CLUSTER_RADIUS_KM = 0.1;
const DEFAULT_MIN_CLUSTER_POINTS = 5;

const HEAT_GRADIENT = ['#2563EB', '#FACC15', '#EF4444'];

function formatDateInput(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toISOString().slice(0, 10);
}

function parseDateInput(value, endOfDay = false) {
  if (!value) return null;
  const suffix = endOfDay ? 'T23:59:59.999Z' : 'T00:00:00.000Z';
  const date = new Date(`${value}${suffix}`);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date;
}

function clamp01(value) {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

function hexToRgb(hex) {
  const clean = hex.replace('#', '');
  const number = Number.parseInt(clean, 16);
  return {
    r: (number >> 16) & 255,
    g: (number >> 8) & 255,
    b: number & 255,
  };
}

function rgbToHex({ r, g, b }) {
  const toHex = (channel) => channel.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function interpolateHex(a, b, t) {
  const start = hexToRgb(a);
  const end = hexToRgb(b);
  const mix = (from, to) => Math.round(from + (to - from) * t);
  return rgbToHex({
    r: mix(start.r, end.r),
    g: mix(start.g, end.g),
    b: mix(start.b, end.b),
  });
}

function heatColor(normalised) {
  const value = clamp01(normalised);
  if (value <= 0.5) {
    const factor = value * 2;
    return interpolateHex(HEAT_GRADIENT[0], HEAT_GRADIENT[1], factor);
  }
  const factor = (value - 0.5) * 2;
  return interpolateHex(HEAT_GRADIENT[1], HEAT_GRADIENT[2], factor);
}

function heatRadius(count, maxCount) {
  if (!Number.isFinite(count) || count <= 0 || !Number.isFinite(maxCount) || maxCount <= 0) {
    return 6;
  }
  const normalised = Math.sqrt(count / maxCount);
  return 8 + normalised * 18;
}

function clusterRadius(points, maxPoints) {
  if (!Number.isFinite(points) || points <= 0 || !Number.isFinite(maxPoints) || maxPoints <= 0) {
    return 10;
  }
  const normalised = Math.sqrt(points / maxPoints);
  return 12 + normalised * 20;
}

export default function FarmGlobalMap() {
  ensureLeafletIcons();

  const { tenant, refreshToken } = useFarmContext();
  const { getToken } = useAuth();
  const [uidData, setUidData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedUid, setSelectedUid] = useState('');
  const [trail, setTrail] = useState({ loading: false, error: '', data: [] });
  const [listFilter, setListFilter] = useState('');
  const [dateRange, setDateRange] = useState(() => {
    const to = new Date();
    const from = new Date(to.getTime() - DEFAULT_WINDOW_DAYS * MS_IN_DAY);
    return { from: formatDateInput(from), to: formatDateInput(to) };
  });
  const [granularity, setGranularity] = useState('1h');
  const [showHeatmap, setShowHeatmap] = useState(true);
  const [showClusters, setShowClusters] = useState(true);
  const [trends, setTrends] = useState({
    loading: false,
    error: '',
    heatmap: [],
    clusters: [],
    meta: null,
  });
  const [activeCluster, setActiveCluster] = useState(null);
  const clusterRadiusKm = DEFAULT_CLUSTER_RADIUS_KM;
  const minClusterPoints = DEFAULT_MIN_CLUSTER_POINTS;

  useEffect(() => {
    setSelectedUid('');
    setTrail({ loading: false, error: '', data: [] });
    setActiveCluster(null);
  }, [tenant]);

  useEffect(() => {
    let cancelled = false;
    async function loadAll() {
      setLoading(true);
      setError('');
      try {
        const token = await getToken();
        if (!token) throw new Error('Missing auth token');

        const all = [];
        let page = 1;
        let total = 0;
        do {
          const params = new URLSearchParams({
            page: String(page),
            pageSize: String(PAGE_SIZE),
            sort: 'lastSeen',
            order: 'desc',
          });
          const response = await fetch(`${API_BASE}/farm/${tenant}/uids?${params.toString()}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            throw new Error(body?.error || `Failed to load map data (${response.status})`);
          }
          const body = await response.json();
          const records = Array.isArray(body.data) ? body.data : [];
          records.forEach(record => {
            if (isValidCoordinate(record?.lastLocation?.lat) && isValidCoordinate(record?.lastLocation?.lon)) {
              all.push(record);
            }
          });
          total = Number(body.total) || records.length;
          page += 1;
        } while (all.length < total && page <= MAX_PAGES && !cancelled);

        if (!cancelled) {
          setUidData(all);
        }
      } catch (err) {
        console.error('Failed to load global map data', err);
        if (!cancelled) {
          setError('Unable to load map data. Please try again.');
          setUidData([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    if (tenant) {
      loadAll();
    }

    return () => {
      cancelled = true;
    };
  }, [tenant, getToken, refreshToken]);

  useEffect(() => {
    if (!tenant) return;
    let cancelled = false;

    async function loadTrends() {
      setTrends(prev => ({
        ...prev,
        loading: true,
        error: '',
      }));
      try {
        const token = await getToken();
        if (!token) throw new Error('Missing auth token');

        const params = new URLSearchParams({
          granularity,
          radius: String(clusterRadiusKm),
          minPoints: String(minClusterPoints),
        });

        const fromDate = parseDateInput(dateRange.from);
        const toDate = parseDateInput(dateRange.to, true);

        if (fromDate) {
          params.set('from', fromDate.toISOString());
        }
        if (toDate) {
          params.set('to', toDate.toISOString());
        }

        const response = await fetch(
          `${API_BASE}/farm/${tenant}/map/trends?${params.toString()}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body?.error || `Failed to load trends (${response.status})`);
        }

        const body = await response.json();
        if (!cancelled) {
          setTrends({
            loading: false,
            error: '',
            heatmap: Array.isArray(body.heatmap) ? body.heatmap : [],
            clusters: Array.isArray(body.clusters) ? body.clusters : [],
            meta: body.meta || null,
          });
        }
      } catch (err) {
        console.error('Failed to load migration trends', err);
        if (!cancelled) {
          setTrends({
            loading: false,
            error: 'Unable to load migration trends. Please try again.',
            heatmap: [],
            clusters: [],
            meta: null,
          });
        }
      }
    }

    loadTrends();
    return () => {
      cancelled = true;
    };
  }, [
    tenant,
    getToken,
    refreshToken,
    dateRange,
    granularity,
    clusterRadiusKm,
    minClusterPoints,
  ]);

  useEffect(() => {
    if (!selectedUid) return;
    let cancelled = false;

    async function loadTrail() {
      setTrail({ loading: true, error: '', data: [] });
      try {
        const token = await getToken();
        if (!token) throw new Error('Missing auth token');
        const response = await fetch(
          `${API_BASE}/farm/${tenant}/uids/${encodeURIComponent(selectedUid)}/trail`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body?.error || `Trail request failed (${response.status})`);
        }
        const body = await response.json();
        if (!cancelled) {
          setTrail({ loading: false, error: '', data: Array.isArray(body.data) ? body.data : [] });
        }
      } catch (err) {
        console.error('Failed to load global trail', err);
        if (!cancelled) {
          setTrail({ loading: false, error: 'Unable to load trail for this device.', data: [] });
        }
      }
    }

    loadTrail();
    return () => {
      cancelled = true;
    };
  }, [selectedUid, tenant, getToken]);

  const markerPoints = useMemo(() => {
    return uidData
      .filter(item => isValidCoordinate(item?.lastLocation?.lat) && isValidCoordinate(item?.lastLocation?.lon))
      .map(item => ({
        uid: item.uid,
        displayName: item.customName?.trim() || item.uid,
        lastSeen: item.lastSeen,
        lat: item.lastLocation.lat,
        lon: item.lastLocation.lon,
        scansCount: item.scansCount,
      }));
  }, [uidData]);

  const activeClusterUidSet = useMemo(() => {
    if (!activeCluster || !Array.isArray(activeCluster.uids) || activeCluster.uids.length === 0) {
      return null;
    }
    return new Set(activeCluster.uids);
  }, [activeCluster]);

  const filteredList = useMemo(() => {
    const raw = listFilter.trim().toLowerCase();
    const source = activeClusterUidSet
      ? markerPoints.filter(item => activeClusterUidSet.has(item.uid))
      : markerPoints;
    if (!raw) return source;
    return source.filter(item => item.displayName.toLowerCase().includes(raw) || item.uid.toLowerCase().includes(raw));
  }, [markerPoints, listFilter, activeClusterUidSet]);

  const selectedPoint = markerPoints.find(item => item.uid === selectedUid);

  const maxHeatCount = useMemo(() => {
    return trends.heatmap.reduce((acc, cell) => {
      const value = Number(cell?.count) || 0;
      return value > acc ? value : acc;
    }, 0);
  }, [trends.heatmap]);

  const maxClusterPoints = useMemo(() => {
    return trends.clusters.reduce((acc, cluster) => {
      const value = Number(cluster?.points) || 0;
      return value > acc ? value : acc;
    }, 0);
  }, [trends.clusters]);

  const corridorLines = useMemo(() => {
    if (!showClusters) return [];
    if (!Array.isArray(trends.clusters) || trends.clusters.length < 2) return [];
    const sorted = [...trends.clusters]
      .sort((a, b) => (b.points || 0) - (a.points || 0))
      .slice(0, 3);
    if (sorted.length < 2) return [];
    const lines = [];
    for (let index = 0; index < sorted.length - 1; index += 1) {
      const current = sorted[index];
      const next = sorted[index + 1];
      if (!current?.center || !next?.center) continue;
      lines.push({
        id: `${current.clusterId}-${next.clusterId}`,
        positions: [
          [current.center.lat, current.center.lon],
          [next.center.lat, next.center.lon],
        ],
      });
    }
    return lines;
  }, [trends.clusters, showClusters]);

  const activeClusterId = activeCluster?.clusterId || '';

  const mapLoading = loading || trends.loading;
  const mapError = error || trends.error;

  useEffect(() => {
    if (!activeClusterUidSet) {
      return;
    }
    if (selectedUid && !activeClusterUidSet.has(selectedUid)) {
      setSelectedUid('');
      setTrail({ loading: false, error: '', data: [] });
    }
  }, [activeClusterUidSet, selectedUid]);

  useEffect(() => {
    if (!showClusters && activeCluster) {
      setActiveCluster(null);
    }
  }, [showClusters, activeCluster]);

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={mapLayoutStyle}>
        <div style={mapPanelStyle}>
          {mapLoading && <p>Loading map...</p>}
          {mapError && <p style={{ color: colors.danger }}>{mapError}</p>}
          {!mapLoading && !mapError && markerPoints.length === 0 && <p>No coordinates available yet.</p>}
          {!mapLoading && !mapError && markerPoints.length > 0 && (
            <div style={mapWrapperStyle}>
              <MapContainer
                center={[markerPoints[0].lat, markerPoints[0].lon]}
                zoom={6}
                style={{ height: '100%', width: '100%' }}
                worldCopyJump
              >
                <TileLayer url={TILE_URL} attribution={ATTRIBUTION} />
                <FitToMarkers markers={markerPoints} trail={trail.data} />

                {trends.heatmap.length > 0 && (
                  <Pane
                    name="heatmap-pane"
                    style={{
                      zIndex: 330,
                      opacity: showHeatmap ? 1 : 0,
                      transition: 'opacity 240ms ease',
                      pointerEvents: 'none',
                    }}
                  >
                    <LayerGroup>
                      {trends.heatmap.map(cell => {
                        const normalised = maxHeatCount ? (cell.count || 0) / maxHeatCount : 0;
                        return (
                          <CircleMarker
                            key={`heat-${cell.lat}-${cell.lon}`}
                            center={[cell.lat, cell.lon]}
                            radius={heatRadius(cell.count || 0, maxHeatCount)}
                            color="transparent"
                            fillColor={heatColor(normalised)}
                            fillOpacity={0.65}
                            opacity={0}
                          />
                        );
                      })}
                    </LayerGroup>
                  </Pane>
                )}

                {trends.clusters.length > 0 && (
                  <Pane
                    name="cluster-pane"
                    style={{
                      zIndex: 340,
                      opacity: showClusters ? 1 : 0,
                      transition: 'opacity 240ms ease',
                      pointerEvents: showClusters ? 'auto' : 'none',
                    }}
                  >
                    <LayerGroup>
                      {trends.clusters.map(cluster => {
                        if (!cluster?.center) return null;
                        const isActiveCluster = cluster.clusterId === activeClusterId;
                        const radiusPx = clusterRadius(cluster.points || 0, maxClusterPoints);
                        return (
                          <CircleMarker
                            key={cluster.clusterId}
                            center={[cluster.center.lat, cluster.center.lon]}
                            radius={radiusPx}
                            color={cluster.color || '#F97316'}
                            weight={isActiveCluster ? 4 : 2}
                            opacity={isActiveCluster ? 0.95 : 0.8}
                            fillColor={cluster.color || '#F97316'}
                            fillOpacity={isActiveCluster ? 0.35 : 0.22}
                            eventHandlers={{
                              click: () => setActiveCluster(cluster),
                            }}
                          >
                            <Tooltip direction="top" sticky>
                              <div style={{ fontSize: '0.85rem' }}>
                                <strong>{cluster.clusterId}</strong>
                                <br />
                                {cluster.uids.length} devices | {cluster.points.toLocaleString()} scans
                              </div>
                            </Tooltip>
                            <Popup>
                              <div style={clusterPopupStyle}>
                                <strong>{cluster.clusterId}</strong>
                                <p style={{ margin: '0.25rem 0 0.5rem' }}>
                                  {cluster.points.toLocaleString()} scans | {cluster.uids.length} devices
                                </p>
                                <p style={{ margin: 0, fontSize: '0.85rem' }}>
                                  UIDs: {cluster.uids.slice(0, 6).join(', ')}
                                  {cluster.uids.length > 6 ? '...' : ''}
                                </p>
                                <button
                                  type="button"
                                  onClick={() => setActiveCluster(cluster)}
                                  style={clusterPopupButtonStyle}
                                >
                                  Focus these UIDs
                                </button>
                              </div>
                            </Popup>
                          </CircleMarker>
                        );
                      })}
                    </LayerGroup>
                    {corridorLines.map(line => (
                      <Polyline
                        key={line.id}
                        positions={line.positions}
                        color="#94A3B8"
                        weight={2}
                        opacity={0.6}
                        dashArray="8 6"
                      />
                    ))}
                  </Pane>
                )}

                <Pane name="device-pane" style={{ zIndex: 380 }}>
                  <LayerGroup>
                    {markerPoints.map(point => {
                      const isActive = point.uid === selectedUid;
                      const inCluster = activeClusterUidSet ? activeClusterUidSet.has(point.uid) : false;
                      const dimmed = Boolean(activeClusterUidSet) && !inCluster;
                      return (
                        <CircleMarker
                          key={point.uid}
                          center={[point.lat, point.lon]}
                          radius={isActive ? 10 : 7}
                          color={isActive ? '#1d9273' : inCluster ? '#0f766e' : '#4b5563'}
                          weight={isActive ? 3 : inCluster ? 2 : 1}
                          opacity={dimmed ? 0.25 : isActive ? 1 : 0.7}
                          fillOpacity={dimmed ? 0.15 : isActive ? 0.9 : inCluster ? 0.65 : 0.5}
                          eventHandlers={{
                            click: () => setSelectedUid(point.uid),
                          }}
                        >
                          <Tooltip direction="top">
                            <div style={{ fontSize: '0.85rem' }}>
                              <strong>{point.displayName}</strong>
                              <br />
                              Last seen: {formatLocal(point.lastSeen)}
                            </div>
                          </Tooltip>
                        </CircleMarker>
                      );
                    })}
                  </LayerGroup>
                </Pane>

                {selectedUid && trail.data.length > 1 && (
                  <Polyline
                    positions={trail.data.map(item => [item.lat, item.lon])}
                    color="#46BB89"
                    weight={5}
                    opacity={0.75}
                  />
                )}
              </MapContainer>

              <div style={mapOverlayStyle}>
                <div style={controlStackStyle}>
                  <div style={controlRowStyle}>
                    <label style={controlLabelStyle}>
                      From
                      <input
                        type="date"
                        value={dateRange.from}
                        max={dateRange.to || undefined}
                        onChange={event =>
                          setDateRange(prev => {
                            const nextFrom = event.target.value;
                            if (prev.to && nextFrom && nextFrom > prev.to) {
                              return { from: nextFrom, to: nextFrom };
                            }
                            return { ...prev, from: nextFrom };
                          })
                        }
                        style={controlInputStyle}
                      />
                    </label>
                    <label style={controlLabelStyle}>
                      To
                      <input
                        type="date"
                        value={dateRange.to}
                        min={dateRange.from || undefined}
                        max={formatDateInput(new Date())}
                        onChange={event =>
                          setDateRange(prev => {
                            const nextTo = event.target.value;
                            if (prev.from && nextTo && nextTo < prev.from) {
                              return { from: nextTo, to: nextTo };
                            }
                            return { ...prev, to: nextTo };
                          })
                        }
                        style={controlInputStyle}
                      />
                    </label>
                  </div>

                  <div style={controlRowStyle}>
                    <label style={controlLabelStyle}>
                      Granularity
                      <select
                        value={granularity}
                        onChange={event => setGranularity(event.target.value)}
                        style={controlInputStyle}
                      >
                        <option value="1h">1 hour</option>
                        <option value="6h">6 hours</option>
                        <option value="1d">1 day</option>
                      </select>
                    </label>
                  </div>

                  <div style={toggleRowStyle}>
                    <label style={toggleLabelStyle}>
                      <input
                        type="checkbox"
                        checked={showHeatmap}
                        onChange={event => setShowHeatmap(event.target.checked)}
                      />
                      <span>Show migration density</span>
                    </label>
                    <label style={toggleLabelStyle}>
                      <input
                        type="checkbox"
                        checked={showClusters}
                        onChange={event => setShowClusters(event.target.checked)}
                      />
                      <span>Show groups/clusters</span>
                    </label>
                  </div>

                  {trends.meta && (
                    <div style={metaSummaryStyle}>
                      <span>
                        {trends.meta.totalPoints?.toLocaleString() || 0} scans | {trends.meta.windowDays ?? DEFAULT_WINDOW_DAYS} day window
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div style={legendContainerStyle}>
                <strong style={{ fontSize: '0.85rem' }}>Legend</strong>
                <div style={legendRowStyle}>
                  <span>Density</span>
                  <div style={legendGradientStyle} />
                </div>
                <div style={legendRowStyle}>
                  <span>Group size</span>
                  <div style={legendCirclesStyle}>
                    <span style={legendCircleStyle(8)} />
                    <span style={legendCircleStyle(14)} />
                    <span style={legendCircleStyle(22)} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <aside style={sidebarStyle}>
          <div style={sidebarHeaderStyle}>
            <h2 style={{ margin: 0 }}>UIDs</h2>
            <button
              type="button"
              onClick={() => {
                setSelectedUid('');
                setTrail({ loading: false, error: '', data: [] });
                setActiveCluster(null);
              }}
              style={clearButtonStyle}
            >
              Clear selection
            </button>
          </div>
          {activeCluster && (
            <div style={clusterFilterNoticeStyle}>
              <div style={{ fontSize: '0.9rem', fontWeight: 500 }}>
                {activeCluster.clusterId} - {activeCluster.uids.length} UIDs | {activeCluster.points.toLocaleString()} scans
              </div>
              <button
                type="button"
                onClick={() => setActiveCluster(null)}
                style={clusterFilterClearStyle}
              >
                Clear group filter
              </button>
            </div>
          )}
          <input
            type="search"
            value={listFilter}
            onChange={event => setListFilter(event.target.value)}
            placeholder="Filter UIDs"
            style={filterInputStyle}
          />
          <div style={uidListStyle}>
            {filteredList.map(item => {
              const isActive = item.uid === selectedUid;
              return (
                <button
                  type="button"
                  key={item.uid}
                  onClick={() => setSelectedUid(prev => (prev === item.uid ? '' : item.uid))}
                  style={uidItemStyle(isActive)}
                >
                  <span style={{ fontWeight: 600 }}>{item.displayName}</span>
                  <span style={{ fontSize: '0.85rem', color: colors.muted }}>
                    {formatLocal(item.lastSeen)}
                  </span>
                  <span style={{ fontSize: '0.8rem', color: colors.muted }}>
                    {formatCoordinate(item.lat)}, {formatCoordinate(item.lon)}
                  </span>
                </button>
              );
            })}
          </div>
          {selectedUid && (
            <div style={selectedSummaryStyle}>
              <h3 style={{ margin: '0 0 0.5rem' }}>{selectedPoint?.displayName || selectedUid}</h3>
              {trail.loading && <p style={{ margin: 0 }}>Loading trail...</p>}
              {trail.error && <p style={{ margin: 0, color: colors.danger }}>{trail.error}</p>}
              {!trail.loading && !trail.error && (
                <p style={{ margin: 0, fontSize: '0.9rem' }}>
                  Points: {trail.data.length.toLocaleString()}
                </p>
              )}
            </div>
          )}
        </aside>
      </div>
    </section>
  );
}

function FitToMarkers({ markers, trail }) {
  const map = useMap();

  useEffect(() => {
    const bounds = [];
    markers.forEach(marker => bounds.push([marker.lat, marker.lon]));
    if (trail && trail.length > 1) {
      trail.forEach(point => bounds.push([point.lat, point.lon]));
    }
    if (!bounds.length) return;
    const mapBounds = L.latLngBounds(bounds);
    map.fitBounds(mapBounds, { maxZoom: 10, padding: [40, 40] });
  }, [markers, trail, map]);

  return null;
}

const mapLayoutStyle = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 2fr) minmax(260px, 1fr)',
  gap: '1.5rem',
};

const mapWrapperStyle = {
  position: 'relative',
  height: '70vh',
  borderRadius: '18px',
  overflow: 'hidden',
  boxShadow: '0 18px 35px rgba(15,23,42,0.18)',
};

const mapOverlayStyle = {
  position: 'absolute',
  top: '16px',
  right: '16px',
  zIndex: 500,
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
};

const controlStackStyle = {
  backgroundColor: 'rgba(17,24,39,0.82)',
  color: '#ffffff',
  borderRadius: '12px',
  padding: '0.75rem',
  minWidth: '220px',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.65rem',
  boxShadow: '0 12px 24px rgba(15,23,42,0.28)',
};

const controlRowStyle = {
  display: 'flex',
  gap: '0.5rem',
  flexWrap: 'wrap',
};

const controlLabelStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.35rem',
  fontSize: '0.85rem',
  flex: '1 1 120px',
};

const controlInputStyle = {
  padding: '0.45rem 0.55rem',
  borderRadius: '8px',
  border: '1px solid rgba(148,163,184,0.4)',
  backgroundColor: 'rgba(15,23,42,0.6)',
  color: '#ffffff',
  fontSize: '0.9rem',
};

const toggleRowStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.35rem',
};

const toggleLabelStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.45rem',
  fontSize: '0.85rem',
};

const metaSummaryStyle = {
  fontSize: '0.8rem',
  color: 'rgba(226,232,240,0.85)',
};

const legendContainerStyle = {
  position: 'absolute',
  bottom: '16px',
  right: '16px',
  zIndex: 500,
  backgroundColor: 'rgba(17,24,39,0.82)',
  color: '#ffffff',
  padding: '0.65rem 0.75rem',
  borderRadius: '12px',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
  minWidth: '180px',
  boxShadow: '0 12px 24px rgba(15,23,42,0.28)',
};

const legendRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '0.65rem',
  fontSize: '0.8rem',
};

const legendGradientStyle = {
  flex: '0 0 90px',
  height: '10px',
  borderRadius: '999px',
  background: 'linear-gradient(90deg, #2563EB 0%, #FACC15 50%, #EF4444 100%)',
};

const legendCirclesStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.45rem',
};

const legendCircleStyle = size => ({
  width: `${size}px`,
  height: `${size}px`,
  borderRadius: '50%',
  border: '2px solid rgba(226,232,240,0.8)',
  backgroundColor: 'transparent',
});

const mapPanelStyle = {
  backgroundColor: 'rgba(70,187,137,0.05)',
  borderRadius: '18px',
  padding: '1rem',
  border: '1px solid rgba(70,187,137,0.18)',
};

const sidebarStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
  backgroundColor: 'white',
  borderRadius: '18px',
  padding: '1rem',
  border: '1px solid rgba(70,187,137,0.18)',
  maxHeight: '70vh',
};

const sidebarHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const clearButtonStyle = {
  backgroundColor: 'transparent',
  border: `1px solid ${colors.border}`,
  borderRadius: '999px',
  padding: '0.3rem 0.8rem',
  cursor: 'pointer',
};

const filterInputStyle = {
  padding: '0.55rem 0.75rem',
  borderRadius: '8px',
  border: `1px solid ${colors.border}`,
  fontSize: '0.95rem',
};

const uidListStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
  overflowY: 'auto',
};

const uidItemStyle = active => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '0.2rem',
  padding: '0.65rem 0.75rem',
  borderRadius: '12px',
  border: active ? '1px solid transparent' : `1px solid rgba(70,187,137,0.15)`,
  backgroundColor: active ? 'rgba(70,187,137,0.2)' : 'rgba(70,187,137,0.08)',
  cursor: 'pointer',
});

const selectedSummaryStyle = {
  padding: '0.75rem',
  borderRadius: '12px',
  backgroundColor: 'rgba(70,187,137,0.12)',
  border: '1px solid rgba(70,187,137,0.2)',
};

const clusterPopupStyle = {
  minWidth: '200px',
  maxWidth: '260px',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
  fontSize: '0.85rem',
};

const clusterPopupButtonStyle = {
  alignSelf: 'flex-start',
  backgroundColor: '#0f766e',
  color: '#ffffff',
  border: 'none',
  borderRadius: '999px',
  padding: '0.3rem 0.75rem',
  cursor: 'pointer',
  fontSize: '0.8rem',
};

const clusterFilterNoticeStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.35rem',
  backgroundColor: 'rgba(70,187,137,0.12)',
  border: '1px solid rgba(70,187,137,0.25)',
  borderRadius: '12px',
  padding: '0.6rem 0.75rem',
  fontSize: '0.85rem',
};

const clusterFilterClearStyle = {
  alignSelf: 'flex-start',
  backgroundColor: 'transparent',
  border: `1px solid ${colors.border}`,
  borderRadius: '999px',
  padding: '0.25rem 0.65rem',
  fontSize: '0.75rem',
  cursor: 'pointer',
};
